import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Card, 
  Form, 
  Input, 
  Button, 
  Select,
  message,
  Typography,
  Avatar,
  Spin,
  Space
} from 'antd';
import { SaveOutlined, UndoOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import PageLayout from '../../../components/layout/PageLayout';
import subscriptionService from '../../../services/subscriptionService';
import { useUser } from '../../../context/UserContext';

const { Title, Text } = Typography;
const { TextArea } = Input;
const { Option } = Select;

const AddPlan = () => {
  const navigate = useNavigate();
  const { user } = useUser();
  const [form] = Form.useForm();
  
  // State management
  const [spinning, setSpinning] = useState(false);

  // Handle form submission
  const handleSubmit = async (values) => {
    try {
      setSpinning(true);
      
      console.log('Submitting plan data:', values);
      const response = await subscriptionService.addSubscriptionPlan(values);
      
      if (response.data && response.data.status) {
        message.success(response.data.message || 'Plan added successfully');
        form.resetFields();
        navigate('/subscriptions/plan');
      } else {
        message.error(response.data.message || 'Failed to add plan');
      }
    } catch (error) {
      console.error('Error adding plan:', error);
      message.error('Failed to add plan');
    } finally {
      setSpinning(false);
    }
  };

  // Handle form reset
  const handleReset = () => {
    form.resetFields();
  };

  // Handle back navigation
  const handleBack = () => {
    navigate('/subscriptions/plan');
  };

  // Only allow numbers
  const onlyNumber = (e) => {
    const charCode = e.which ? e.which : e.keyCode;
    if (
      charCode !== 8 && // backspace
      charCode !== 46 && // delete
      charCode !== 37 && // left arrow
      charCode !== 39 && // right arrow
      (charCode < 48 || charCode > 57)
    ) {
      e.preventDefault();
    }
  };

  // Header content with user info
  const headerContent = (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '16px' }}>
        <Avatar 
          src={user?.avatar} 
          size={64} 
          style={{ marginRight: '16px' }}
        >
          {user?.name?.charAt(0)?.toUpperCase()}
        </Avatar>
        <div>
          <Title level={4} style={{ margin: 0, marginBottom: '4px' }}>
            {user?.name || 'Admin'}
          </Title>
          <Text type="secondary">Add New Subscription Plan</Text>
        </div>
      </div>
    </div>
  );

  return (
    <PageLayout
      title="Add Subscription Plan"
      desc="Create a new subscription plan with all details"
      avatar={user?.avatar}
      headerContent={headerContent}
    >
      <Spin spinning={spinning}>
        <Card>
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
            initialValues={{
              is_free_plan: 'N',
              is_trial_available: 'N',
              plan_is_active: 'Y',
              years: 0,
              months: 0,
              days: 0,
              additional_years: 0,
              additional_months: 0,
              additional_days: 0
            }}
          >
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              <Form.Item
                name="amazon_iap_parent_sku"
                label="Amazon IAP Parent SKU"
              >
                <Input placeholder="Enter Amazon IAP parent SKU" />
              </Form.Item>

              <Form.Item
                name="amazon_iap_sku"
                label="Amazon IAP SKU"
              >
                <Input placeholder="Enter Amazon IAP SKU" />
              </Form.Item>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              <Form.Item
                name="plan_name"
                label="Plan Name"
                rules={[{ required: true, message: 'Please enter plan name' }]}
              >
                <Input placeholder="Enter plan name" />
              </Form.Item>

              <Form.Item
                name="plan_order"
                label="Plan Order"
                rules={[{ required: true, message: 'Please enter plan order' }]}
              >
                <Input 
                  type="number" 
                  placeholder="Enter plan order"
                  onKeyPress={onlyNumber}
                />
              </Form.Item>
            </div>

            <Form.Item
              name="plan_description"
              label="Plan Description"
            >
              <TextArea 
                rows={4} 
                placeholder="Enter plan description"
                maxLength={2000}
                showCount
              />
            </Form.Item>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px' }}>
              <Form.Item
                name="years"
                label="Plan Duration Years"
                rules={[{ required: true, message: 'Please enter years' }]}
              >
                <Input 
                  type="number" 
                  placeholder="Enter years"
                  onKeyPress={onlyNumber}
                />
              </Form.Item>

              <Form.Item
                name="months"
                label="Plan Duration Months"
                rules={[{ required: true, message: 'Please enter months' }]}
              >
                <Input 
                  type="number" 
                  placeholder="Enter months"
                  onKeyPress={onlyNumber}
                />
              </Form.Item>

              <Form.Item
                name="days"
                label="Plan Duration Days"
                rules={[{ required: true, message: 'Please enter days' }]}
              >
                <Input 
                  type="number" 
                  placeholder="Enter days"
                  onKeyPress={onlyNumber}
                />
              </Form.Item>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px' }}>
              <Form.Item
                name="additional_years"
                label="Additional Years"
              >
                <Input 
                  type="number" 
                  placeholder="Enter additional years"
                  onKeyPress={onlyNumber}
                />
              </Form.Item>

              <Form.Item
                name="additional_months"
                label="Additional Months"
              >
                <Input 
                  type="number" 
                  placeholder="Enter additional months"
                  onKeyPress={onlyNumber}
                />
              </Form.Item>

              <Form.Item
                name="additional_days"
                label="Additional Days"
              >
                <Input 
                  type="number" 
                  placeholder="Enter additional days"
                  onKeyPress={onlyNumber}
                />
              </Form.Item>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              <Form.Item
                name="plan_amt"
                label="Plan Amount"
                rules={[{ required: true, message: 'Please enter plan amount' }]}
              >
                <Input 
                  type="number" 
                  placeholder="Enter plan amount"
                  onKeyPress={onlyNumber}
                />
              </Form.Item>

              <Form.Item
                name="plan_is_active"
                label="Status"
                rules={[{ required: true, message: 'Please select status' }]}
              >
                <Select placeholder="Select status">
                  <Option value="Y">Active</Option>
                  <Option value="N">Inactive</Option>
                </Select>
              </Form.Item>
            </div>

            <Form.Item>
              <Space>
                <Button 
                  type="primary" 
                  htmlType="submit" 
                  icon={<SaveOutlined />}
                  loading={spinning}
                  size="large"
                >
                  Add Plan
                </Button>
                <Button 
                  onClick={handleReset}
                  icon={<UndoOutlined />}
                  size="large"
                >
                  Reset
                </Button>
                <Button 
                  onClick={handleBack}
                  icon={<ArrowLeftOutlined />}
                  size="large"
                >
                  Go Back
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </Card>
      </Spin>
    </PageLayout>
  );
};

export default AddPlan;